import { Alert, AlertColor, Box, Button, CircularProgress, DialogActions, DialogContent, Typography } from "@mui/material";
import { formatCurrency } from "../../../utils/formatCurrency";
import { TerminalAttempt, isOpenAttempt } from "./terminalPaymentApi";
import { buildAttemptMessage } from "./terminalPaymentMessages";
import { useTerminalPayment } from "./useTerminalPayment";
import { SplitPaymentRows } from "../split-payment/SplitPaymentRows";
import { ChargeProgress, buildSplitPaymentsNote } from "../split-payment/splitPayments";
import { useSplitPayments } from "../split-payment/useSplitPayments";

const TERMINAL_PAYMENT_METHOD = "multibanco";

type TerminalPaymentDialogProps = {
    orderId: number;
    clinicName: string;
    orderTotal: number;
    onPaid: () => void;
    onClose: () => void;
};

const RESULT_SEVERITY: Record<TerminalAttempt["status"], AlertColor> = {
    pending: "info",
    processing: "info",
    paid: "success",
    declined: "error",
    failed: "error",
    expired: "warning",
    cancelled: "warning",
};

const resolveChargeProgress = (attempt: TerminalAttempt) => {
    if (isOpenAttempt(attempt)) return "open";
    const chargeProgress: ChargeProgress = attempt.status === "paid" ? "paid" : "closed";
    return chargeProgress;
};

const formatCountdown = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    const countdown = `${minutes}:${seconds}`;
    return countdown;
};

export const TerminalPaymentDialog = ({ orderId, clinicName, orderTotal, onPaid, onClose }: TerminalPaymentDialogProps) => {
    const terminalPayment = useTerminalPayment(orderId, onPaid);
    const { attempt, screen } = terminalPayment;
    const splitPayments = useSplitPayments(orderTotal, TERMINAL_PAYMENT_METHOD, screen === "amount", attempt);
    const attemptSplitPayments = attempt?.split_payments ?? [];

    return (
        <>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="body1" color="primary">
                    Terminal Revolut — {clinicName}
                </Typography>

                {screen === "loading" && (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress />
                    </Box>
                )}

                {screen === "amount" && (
                    <>
                        <Typography variant="h6">
                            {splitPayments.rows.length
                                ? `Valor a cobrar no terminal: ${formatCurrency(splitPayments.remainingAmount)}`
                                : `Valor a cobrar: ${formatCurrency(orderTotal)}`}
                        </Typography>
                        <SplitPaymentRows rows={splitPayments.rows} onChange={splitPayments.setRows} />
                        {splitPayments.visibleError && <Alert severity="warning">{splitPayments.visibleError}</Alert>}
                    </>
                )}

                {screen === "waiting" && attempt && (
                    <Box display="flex" alignItems="center" gap={2}>
                        <CircularProgress size={28} />
                        <Box>
                            <Typography variant="body1">{buildAttemptMessage(attempt)}</Typography>
                            {attempt.status === "pending" && (
                                <Typography variant="body2" color="text.secondary">
                                    Tempo restante: {formatCountdown(terminalPayment.secondsRemaining)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {screen === "result" && attempt && <Alert severity={RESULT_SEVERITY[attempt.status]}>{buildAttemptMessage(attempt)}</Alert>}

                {screen !== "amount" && attempt && attemptSplitPayments.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {buildSplitPaymentsNote(attemptSplitPayments, resolveChargeProgress(attempt), attempt.recording_status)}
                    </Typography>
                )}

                {terminalPayment.errorMessage && screen !== "result" && <Alert severity="error">{terminalPayment.errorMessage}</Alert>}
            </DialogContent>

            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    Fechar
                </Button>
                {screen === "amount" && (
                    <Button
                        onClick={() => terminalPayment.startCharge(splitPayments.payments)}
                        disabled={terminalPayment.isSubmitting || !splitPayments.validation.isValid}
                    >
                        {terminalPayment.isSubmitting ? <CircularProgress color="inherit" size={24} /> : "Enviar para o terminal"}
                    </Button>
                )}
                {screen === "waiting" && (
                    <Button
                        color="error"
                        onClick={terminalPayment.cancelCharge}
                        disabled={attempt?.status !== "pending" || terminalPayment.isCancelling}
                    >
                        Cancelar
                    </Button>
                )}
                {screen === "result" && attempt?.status !== "paid" && <Button onClick={terminalPayment.openAmountForm}>Tentar novamente</Button>}
            </DialogActions>
        </>
    );
};
